import { BadgeList } from '@/components/Atoms/BadgeList/BadgeList';
import { LocalizedDate } from '@/components/Atoms/LocalizedDate/LocalizedDate';
import { MarkedText } from '@/components/Atoms/MarkedText/MarkedText';
import { SkeletonPulse } from '@/components/Atoms/SkeletonPulse/SkeletonPulse';
import { H5 } from '@/components/Core/Heading';
import { Card, CardActions, CardBody, CardDescription, CardMetadata } from '@/components/Molecules/Card/Card';
import { ContentPin } from '@/components/Molecules/ContentPin/ContentPin';
import { ContentStatus } from '@/components/Molecules/ContentStatus/ContentStatus';
import { ToolTypeHeader } from '@/components/Molecules/ContentTypeHeader/ContentTypeHeader';
import { SaveButton } from '@/components/Molecules/SaveButton/SaveButton';
import { Stack } from '@/components/Molecules/Stack/Stack';
import useTranslation from '@/hooks/useTranslation';
import type { IAnalyticsTraceableElement } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import type { ToolSavedSearch } from '@/services/UserActivity/ToolSavedSearchScheduler';
import { ToolSavedSearchScheduler } from '@/services/UserActivity/ToolSavedSearchScheduler';
import ClockIcon from '@bloomberg/web-icons-react/Clock';
import type { Tool } from 'bnef';
import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import type { FCl } from 'react-fc';
import styles from './ToolCard.module.css';

interface IProps extends IAnalyticsTraceableElement {
    tool: Tool;
    pinned?: boolean;
    allowSave?: boolean; // hack for #geoeconomics
    showDescription?: boolean;
    showToolSavedSearches?: boolean;
    showImage?: boolean;
}

export const ToolCard: FCl<IProps> = ({
    tool,
    pinned,
    analyticsBaseId,
    analyticsCategory = TrackingCategory.DataAndTools,
    className,
    allowSave = true,
    position,
    showDescription = true,
    showToolSavedSearches = true,
    showImage = false,
}) => {
    const { t } = useTranslation();
    const titleId = useId();
    let prefix = null;
    const { title, description, contentStatus, url } = tool;
    const [toolSavedSearches, setToolSavedSearches] = useState<ToolSavedSearch | null>();
    const supportsSavedSearches = tool.subtype === 'dataset';

    useEffect(() => {
        if (showToolSavedSearches && supportsSavedSearches) {
            ToolSavedSearchScheduler.getInstance().registerItem(tool.url, (x) => {
                setToolSavedSearches(x);
            });
        }
    }, [tool.url]);

    if (tool.subtype) {
        prefix = (
            <BadgeList>
                <ToolTypeHeader subtype={tool.subtype} />
                {pinned && <ContentPin />}
                <ContentStatus status={contentStatus} />
            </BadgeList>
        );
    }

    let cardTitle = title;
    if (tool.parent) {
        cardTitle = `${tool.parent.title}: ${cardTitle}`;
    }

    const savedSearches = toolSavedSearches?.savedSearches ?? [];
    const shouldShowDescription = Boolean(description && showDescription);
    const shouldShowSavedSearches = Boolean(showToolSavedSearches && supportsSavedSearches && savedSearches.length > 0);
    const shouldShowSavedSearchesSkeleton =
        toolSavedSearches === undefined && showToolSavedSearches && supportsSavedSearches;
    const shouldRenderCardBody = shouldShowDescription || shouldShowSavedSearches || shouldShowSavedSearchesSkeleton;

    let toolSavedSearchesElement: ReactNode = null;
    if (shouldShowSavedSearches && toolSavedSearches) {
        toolSavedSearchesElement = (
            <Stack key="searches" $gap="var(--size-grid-half)">
                <H5 as="h4" className={styles.metadataHeader}>
                    {t(
                        toolSavedSearches.visibility === 'private'
                            ? 'common:Labels.userViews'
                            : 'common:Labels.popularViews'
                    )}
                </H5>
                <Stack $gap="var(--size-grid-half)" className="cds-t-body" as="ul">
                    {savedSearches.map((savedSearch) => (
                        <li className={styles.listItem} key={savedSearch.id}>
                            <a
                                href={savedSearch.url}
                                data-analytics-tracking={buildTrackingObject(
                                    TrackingAction.FollowLink,
                                    analyticsCategory,
                                    'Saved Search',
                                    position
                                )}
                            >
                                {savedSearch.label}
                            </a>
                        </li>
                    ))}
                </Stack>
            </Stack>
        );
    } else if (shouldShowSavedSearchesSkeleton) {
        toolSavedSearchesElement = <SkeletonPulse key={tool.id} />;
    }

    return (
        <Card
            id={titleId}
            className={className}
            key={url}
            title={<MarkedText value={cardTitle} />}
            href={url}
            prefix={prefix}
            linkProps={{
                'data-analytics-tracking': buildTrackingObject(
                    TrackingAction.FollowLink,
                    analyticsCategory,
                    analyticsBaseId,
                    position
                ),
            }}
            withImage={showImage}
        >
            {shouldRenderCardBody && (
                <CardBody key="body">
                    {shouldShowDescription && (
                        <CardDescription key="description" lines={4}>
                            <MarkedText value={description} />
                        </CardDescription>
                    )}
                    {tool.subtype === 'dataset' && tool.displayDate && (
                        <CardMetadata
                            items={[
                                {
                                    icon: <ClockIcon />,
                                    label: t('common:Labels.lastUpdateDate'),
                                    value: <LocalizedDate date={tool.displayDate} />,
                                },
                            ]}
                        />
                    )}
                    {toolSavedSearchesElement}
                </CardBody>
            )}
            {allowSave && (
                <CardActions key="save">
                    <SaveButton
                        aria-describedby={titleId}
                        id={tool.id}
                        type="tool"
                        itemName={title}
                        analyticsBaseId={analyticsBaseId}
                    />
                </CardActions>
            )}
        </Card>
    );
};
